import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import {
  Modal,
  Form,
  Button,
  Input,
  Checkbox,
  Label,
  Segment,
  Divider,
  Icon,
  Message,
} from 'semantic-ui-react';
import ReactQuill from 'react-quill';

import { getInfluencerFromOrganisations } from '../../utils/helper-functions';
import { authService } from '../../utils/auth-service';

const RECIPIENT_MODES = {
  all: 'all',
  workflow: 'workflow',
  manual: 'manual',
};

const getRecipient = discussion => {
  const influencer = discussion.influencer
    || getInfluencerFromOrganisations(discussion.organisations);
  return {
    discussionId: discussion.id,
    name: discussion.name || _.get(influencer, 'name', ''),
    email: _.get(influencer, 'email', ''),
    workflowStatus: discussion.workflowStatus || '',
  };
};

const stripHtml = html => (html || '').replace(/<(.|\n)*?>/g, '').trim();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmailFormat = e => typeof e === 'string' && EMAIL_REGEX.test(_.trim(e));

// Variables shown as clickable chips in the UI.
const VARIABLES = [
  { name: 'name', desc: "Recipient's name" },
  { name: 'campaign_name', desc: 'Campaign name' },
];
// Full set the Lambda actually substitutes at send time. Keep in sync with
// SUPPORTED_VARIABLES in dyzio-email-sender/lib/variables.js.
const SUPPORTED_VARIABLE_NAMES = ['name', 'campaign_name'];
const VARIABLE_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

const tabStyle = isActive => (isActive ? {
  backgroundColor: '#fff5f8',
  color: '#ed1f7e',
  boxShadow: '0 0 0 1px #ed1f7e inset',
  fontWeight: 600,
} : null);

const findUnknownVariables = (...templates) => {
  const found = new Set();
  templates.forEach(tpl => {
    if (!tpl) return;
    let match;
    VARIABLE_RE.lastIndex = 0;
    // eslint-disable-next-line no-cond-assign
    while ((match = VARIABLE_RE.exec(tpl)) !== null) {
      if (!SUPPORTED_VARIABLE_NAMES.includes(match[1])) found.add(match[1]);
    }
  });
  return [...found];
};

const INITIAL_FORM_STATE = {
  recipientMode: RECIPIENT_MODES.all,
  selectedWorkflowStatuses: [],
  selectedDiscussionIds: [],
  fromName: '',
  fromEmail: '',
  replyTo: '',
  subject: '',
  body: '',
  filterTerm: '',
};

class AdHocEmailModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...INITIAL_FORM_STATE,
      isSending: false,
      confirmingSend: false,
      // 'subject' | 'body' — chips insert into whichever was last focused.
      activeField: 'body',
      // Only mark a field "touched" after first blur — errors don't show while
      // the user is typing for the first time. Once touched, error visibility
      // tracks validity normally.
      touched: { fromName: false, fromEmail: false, replyTo: false },
      // { type: 'success' | 'partial' | 'error' | 'test-success', summary?, message?, sentTo? }
      lastResult: null,
    };
    this._isMounted = false;
    // Synchronous lock — state updates are async, so a fast double-click
    // can fire two onClick handlers before isSending re-renders. This guard
    // sits at the top of every send handler.
    this._sending = false;
    // Callback refs (React 15 doesn't have React.createRef).
    this._quillInstance = null;
    // Last known Quill cursor — captured on selection-change so we can still
    // insert at the right spot after the chip steals focus.
    this._lastQuillSelection = null;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  setRecipientMode = mode => () => this.setState({ recipientMode: mode });

  toggleWorkflowStatus = status => () => {
    this.setState(prev => {
      const current = prev.selectedWorkflowStatuses;
      const next = current.includes(status)
        ? current.filter(s => s !== status)
        : [...current, status];
      return { selectedWorkflowStatuses: next };
    });
  }

  toggleDiscussionId = id => () => {
    this.setState(prev => {
      const current = prev.selectedDiscussionIds;
      const next = current.includes(id)
        ? current.filter(d => d !== id)
        : [...current, id];
      return { selectedDiscussionIds: next };
    });
  }

  toggleSelectAll = filteredDiscussions => () => {
    const ids = filteredDiscussions.map(d => d.id);
    this.setState(prev => {
      const allSelected = ids.every(id => prev.selectedDiscussionIds.includes(id));
      const next = allSelected
        ? prev.selectedDiscussionIds.filter(id => !ids.includes(id))
        : _.uniq([...prev.selectedDiscussionIds, ...ids]);
      return { selectedDiscussionIds: next };
    });
  }

  markTouched = field => () => {
    this.setState(prev => ({ touched: { ...prev.touched, [field]: true } }));
  }

  onSubjectChange = (e, { value }) => this.setState({ subject: value });

  onSubjectFocus = () => this.setState({ activeField: 'subject' });

  onBodyFocus = () => this.setState({ activeField: 'body' });

  // Insert a variable (`{{name}}`) into whichever field had focus most recently.
  // Defaults to body if no field has been focused yet.
  insertVariable = name => () => {
    const variable = `{{${name}}}`;
    if (this.state.activeField === 'subject') {
      this.insertVariableIntoSubject(variable);
    } else {
      this.insertVariableIntoBody(variable);
    }
  }

  // Old semantic-ui-react Input (0.74) doesn't surface the underlying <input>
  // through ref cleanly enough for cursor-aware inserts in React 15, so we
  // append at the end. Subject is a single line — usually fine.
  insertVariableIntoSubject = variable => {
    this.setState(prev => ({ subject: `${prev.subject || ''}${variable}` }));
  }

  onQuillSelectionChange = range => { this._lastQuillSelection = range; }

  insertVariableIntoBody = variable => {
    const editor = this._quillInstance && this._quillInstance.getEditor && this._quillInstance.getEditor();
    if (!editor) {
      this.setState(prev => ({ body: `${prev.body || ''}${variable}` }));
      return;
    }
    // Quill always has a trailing newline; getLength() points AFTER it, which
    // would put the insertion on a new line. Subtract 1 to land on the last
    // existing line. If the user had a real cursor (captured via
    // selection-change), prefer that.
    const live = editor.getSelection();
    const remembered = this._lastQuillSelection;
    const fallback = Math.max(0, editor.getLength() - 1);
    const range = live || remembered || { index: fallback, length: 0 };
    editor.insertText(range.index, variable, 'user');
    editor.setSelection(range.index + variable.length, 0, 'user');
    this._lastQuillSelection = { index: range.index + variable.length, length: 0 };
  }

  onFromNameChange = (e, { value }) => this.setState({ fromName: value });

  onFromEmailChange = (e, { value }) => this.setState({ fromEmail: value });

  onReplyToChange = (e, { value }) => this.setState({ replyTo: value });

  onBodyChange = body => this.setState({ body });

  onFilterChange = (e, { value }) => this.setState({ filterTerm: value });

  resolveRecipients = () => {
    const { discussions } = this.props;
    const {
      recipientMode,
      selectedWorkflowStatuses,
      selectedDiscussionIds,
    } = this.state;

    let picked;
    if (recipientMode === RECIPIENT_MODES.all) {
      picked = discussions.map(getRecipient);
    } else if (recipientMode === RECIPIENT_MODES.workflow) {
      if (_.isEmpty(selectedWorkflowStatuses)) return [];
      picked = discussions
        .filter(d => selectedWorkflowStatuses.includes(d.workflowStatus))
        .map(getRecipient);
    } else {
      picked = discussions
        .filter(d => selectedDiscussionIds.includes(d.id))
        .map(getRecipient);
    }

    // Dedupe by email (case-insensitive, whitespace-tolerant).
    return _.uniqBy(picked, r => (r.email || '').toLowerCase().trim());
  }

  postToLambda = async ({ recipients, isTest }) => {
    const { campaignId, campaignName } = this.props;
    const { subject, body, fromName, fromEmail, replyTo } = this.state;

    // Sender identity + organisationId are NOT sent in the body — the Lambda
    // derives them from the verified JWT in the Authorization header. Anything
    // identity-related the body claimed would be ignored on the backend.
    const payload = {
      campaignId,
      campaignName,
      fromName: _.trim(fromName) || null,
      fromEmail: _.trim(fromEmail) || null,
      replyTo: _.trim(replyTo) || null,
      subject,
      bodyHtml: body,
      recipients,
      isTest: !!isTest,
    };

    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    if (!url) {
      // Lambda not deployed yet — log and pretend success so the UI is testable.
      // eslint-disable-next-line no-console
      console.log('[ad-hoc-email] (no REACT_APP_AD_HOC_EMAIL_URL set) would post', payload);
      return { data: { sendId: 'local-noop', queued: recipients.length, skipped: 0, status: 'QUEUED' } };
    }

    const token = authService.getToken();
    const response = await axios.post(`${url}/ad-hoc-email`, payload, {
      timeout: 30000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  }

  // First click on "Send now" — open the confirmation step instead of sending immediately.
  handleSendClick = () => {
    if (this._sending || this.state.isSending) return;
    this.setState({ confirmingSend: true });
  }

  handleCancelConfirm = () => this.setState({ confirmingSend: false });

  // Actual POST happens after the user confirms.
  handleConfirmSend = async () => {
    if (this._sending || this.state.isSending) return;
    this._sending = true;
    this.setState({ isSending: true, lastResult: null });
    const recipients = this.resolveRecipients();
    try {
      const response = await this.postToLambda({ recipients, isTest: false });
      const summary = _.get(response, 'data', {});
      // Backend is async now: 200 means "accepted and queued". Per-recipient
      // outcomes show up later in the email-history modal.
      if (this._isMounted) {
        if (summary.queued > 0) {
          // Reset the form so accidental re-sends are very deliberate.
          this.setState({
            ...INITIAL_FORM_STATE,
            isSending: false,
            confirmingSend: false,
            touched: { fromName: false, fromEmail: false, replyTo: false },
            lastResult: {
              type: 'queued',
              queued: summary.queued,
              skipped: summary.skipped || 0,
            },
          });
        } else {
          // 0 queued — every recipient had an invalid email. Should be rare
          // because validation blocks empty recipient counts upstream.
          this.setState({
            isSending: false,
            confirmingSend: false,
            lastResult: { type: 'error', message: 'No valid recipients to send to.' },
          });
        }
      }
    } catch (err) {
      console.error('[ad-hoc-email] send failed', err);
      if (this._isMounted) {
        this.setState({
          isSending: false,
          confirmingSend: false,
          lastResult: { type: 'error', message: _.get(err, 'response.data.error') || err.message || 'Send failed' },
        });
      }
    } finally {
      this._sending = false;
    }
  }

  handleSendTestToMe = async () => {
    if (this._sending || this.state.isSending) return;
    this._sending = true;
    this.setState({ isSending: true, lastResult: null });
    const { currentUserEmail, currentUserName } = this.props;
    const recipients = [{
      discussionId: null,
      name: currentUserName || currentUserEmail,
      email: currentUserEmail,
      workflowStatus: '',
    }];
    try {
      const response = await this.postToLambda({ recipients, isTest: true });
      const summary = _.get(response, 'data', {});
      if (this._isMounted) {
        const queued = summary.queued > 0;
        this.setState({
          isSending: false,
          lastResult: {
            type: queued ? 'test-queued' : 'error',
            message: queued ? null : 'Test send failed',
          },
        });
      }
    } catch (err) {
      console.error('[ad-hoc-email] test send failed', err);
      if (this._isMounted) {
        this.setState({
          isSending: false,
          lastResult: { type: 'error', message: _.get(err, 'response.data.error') || err.message || 'Test send failed' },
        });
      }
    } finally {
      this._sending = false;
    }
  }

  render() {
    const { campaignName, discussions, currentUserEmail, onClose } = this.props;
    const {
      recipientMode,
      selectedWorkflowStatuses,
      selectedDiscussionIds,
      fromName,
      fromEmail,
      replyTo,
      subject,
      body,
      filterTerm,
      isSending,
      confirmingSend,
      activeField,
      touched,
      lastResult,
    } = this.state;

    const workflowStatusCounts = _.countBy(
      discussions.filter(d => !!d.workflowStatus),
      'workflowStatus',
    );
    const workflowStatuses = Object.keys(workflowStatusCounts).sort();

    // Strip HTML from body so a `{{variable}}` split across formatting tags is still detectable.
    const unknownVariables = findUnknownVariables(subject, stripHtml(body));

    const term = _.trim(filterTerm).toLowerCase();
    const filteredDiscussions = term
      ? discussions.filter(d => {
        const recipient = getRecipient(d);
        return (
          (recipient.name || '').toLowerCase().includes(term)
          || (recipient.email || '').toLowerCase().includes(term)
          || (recipient.workflowStatus || '').toLowerCase().includes(term)
        );
      })
      : discussions;

    const recipients = this.resolveRecipients();
    const recipientCount = recipients.length;
    const recipientsWithoutEmail = recipients.filter(r => !r.email).length;
    const hasSubject = !_.isEmpty(_.trim(subject));
    const hasBody = !_.isEmpty(stripHtml(body));
    const hasFromName = !_.isEmpty(_.trim(fromName));
    const hasFromEmail = isValidEmailFormat(fromEmail);
    const hasReplyTo = isValidEmailFormat(replyTo);
    const allHeadersFilled = hasFromName && hasFromEmail && hasReplyTo;
    const canSend = allHeadersFilled && hasSubject && hasBody && recipientCount > 0;
    const canSendTest = allHeadersFilled && hasSubject && hasBody && currentUserEmail;

    const isAllMode = recipientMode === RECIPIENT_MODES.all;
    const isWorkflowMode = recipientMode === RECIPIENT_MODES.workflow;
    const isManualMode = recipientMode === RECIPIENT_MODES.manual;

    return (
      <Modal open size="large" onClose={onClose} closeOnEscape closeOnDimmerClick={false}>
        <Modal.Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div>Send ad-hoc email</div>
              <div className="small light" style={{ fontWeight: 'normal', fontSize: 13, marginTop: 2 }}>
                Campaign · {campaignName || '—'}
              </div>
            </div>
            <Icon name="close" link onClick={onClose} style={{ marginTop: 4 }} />
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          <Form>
            <Form.Field>
              <label>Recipients</label>
              <Button.Group basic fluid>
                <Button
                  type="button"
                  active={isAllMode}
                  style={tabStyle(isAllMode)}
                  onClick={this.setRecipientMode(RECIPIENT_MODES.all)}
                >
                  All collaborations
                </Button>
                <Button
                  type="button"
                  active={isWorkflowMode}
                  style={tabStyle(isWorkflowMode)}
                  onClick={this.setRecipientMode(RECIPIENT_MODES.workflow)}
                >
                  By workflow status
                </Button>
                <Button
                  type="button"
                  active={isManualMode}
                  style={tabStyle(isManualMode)}
                  onClick={this.setRecipientMode(RECIPIENT_MODES.manual)}
                >
                  Pick people
                </Button>
              </Button.Group>
            </Form.Field>

            {isAllMode && (() => {
              const allRecipients = discussions.map(getRecipient);
              const withEmail = allRecipients.filter(r => r.email);
              const uniqueByEmail = _.uniqBy(withEmail, r => r.email.toLowerCase().trim());
              const duplicateCount = withEmail.length - uniqueByEmail.length;
              const noEmailCount = allRecipients.length - withEmail.length;

              const previewNames = allRecipients
                .slice(0, 5)
                .map(r => r.name || r.email || '(no name)')
                .filter(Boolean);
              const remaining = Math.max(0, allRecipients.length - previewNames.length);

              if (allRecipients.length === 0) {
                return (
                  <Message size="tiny" warning style={{ marginTop: 8, marginBottom: 16 }}>
                    <Icon name="info circle" />
                    No collaborations on this campaign yet.
                  </Message>
                );
              }
              return (
                <div style={{
                  background: '#f9fafb',
                  border: '1px solid #eee',
                  borderRadius: 4,
                  padding: '12px 16px',
                  marginTop: 8,
                  marginBottom: 16,
                  fontSize: 13,
                }}
                >
                  <div>
                    Will send to{' '}
                    <strong>{uniqueByEmail.length} unique recipient{uniqueByEmail.length === 1 ? '' : 's'}</strong>
                  </div>
                  <div style={{ color: '#888', marginTop: 4, fontSize: 12 }}>
                    From {allRecipients.length} collaboration{allRecipients.length === 1 ? '' : 's'}: {previewNames.join(', ')}
                    {remaining > 0 && ` +${remaining} more`}
                  </div>
                  {duplicateCount > 0 && (
                    <div style={{ color: '#888', marginTop: 4, fontSize: 12 }}>
                      <Icon name="copy outline" />
                      {duplicateCount} duplicate email{duplicateCount === 1 ? '' : 's'} merged
                    </div>
                  )}
                  {noEmailCount > 0 && (
                    <div style={{ color: '#a36b00', marginTop: 4, fontSize: 12 }}>
                      <Icon name="warning sign" />
                      {noEmailCount} have no email — will be skipped
                    </div>
                  )}
                </div>
              );
            })()}

            {isWorkflowMode && (
              <Segment>
                {_.isEmpty(workflowStatuses) && (
                  <p className="small light">No workflow statuses found on this campaign.</p>
                )}
                {!_.isEmpty(workflowStatuses) && (
                  <div>
                    {workflowStatuses.map(status => (
                      <div key={status} style={{ marginBottom: 6 }}>
                        <Checkbox
                          label={`${status} (${workflowStatusCounts[status]})`}
                          checked={selectedWorkflowStatuses.includes(status)}
                          onChange={this.toggleWorkflowStatus(status)}
                        />
                      </div>
                    ))}
                    {!_.isEmpty(selectedWorkflowStatuses) && (() => {
                      const matchingDiscussions = discussions.filter(d => selectedWorkflowStatuses.includes(d.workflowStatus));
                      const dupes = matchingDiscussions.length - recipients.length;
                      return (
                        <div>
                          <Divider />
                          <p className="small light" style={{ marginBottom: 6 }}>
                            Will send to <strong>{recipients.length} unique recipient{recipients.length === 1 ? '' : 's'}</strong>
                            {dupes > 0 && (
                              <span style={{ color: '#888' }}>
                                {' '}(from {matchingDiscussions.length} matching collaborations, {dupes} duplicate email{dupes === 1 ? '' : 's'} merged)
                              </span>
                            )}
                          </p>
                          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                            {recipients.map(r => (
                              <div
                                key={r.discussionId}
                                style={{ padding: '4px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                              >
                                <span>
                                  {r.name}{r.email ? ` <${r.email}>` : ' (no email)'}
                                </span>
                                {r.workflowStatus
                                  && <Label size="tiny" basic>{r.workflowStatus}</Label>}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </Segment>
            )}

            {isManualMode && (
              <Segment>
                <Input
                  fluid
                  icon="search"
                  placeholder="Filter by name, email, or status…"
                  value={filterTerm}
                  onChange={this.onFilterChange}
                />
                <Divider hidden />
                {selectedDiscussionIds.length > 0 && (
                  <div style={{ marginBottom: 8, fontSize: 13 }}>
                    Will send to{' '}
                    <strong>{recipients.length} unique recipient{recipients.length === 1 ? '' : 's'}</strong>
                    {selectedDiscussionIds.length > recipients.length && (
                      <span style={{ color: '#888' }}>
                        {' '}({selectedDiscussionIds.length - recipients.length} duplicate email{selectedDiscussionIds.length - recipients.length === 1 ? '' : 's'} merged)
                      </span>
                    )}
                  </div>
                )}
                {!_.isEmpty(filteredDiscussions) && (
                  <div style={{ marginBottom: 8 }}>
                    <Checkbox
                      label={`Select all (${filteredDiscussions.length})`}
                      checked={
                        filteredDiscussions.length > 0
                        && filteredDiscussions.every(d => selectedDiscussionIds.includes(d.id))
                      }
                      onChange={this.toggleSelectAll(filteredDiscussions)}
                    />
                  </div>
                )}
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {_.isEmpty(filteredDiscussions) && (
                    <p className="small light">
                      {discussions.length === 0
                        ? 'No collaborations on this campaign yet.'
                        : 'No matches for that filter.'}
                    </p>
                  )}
                  {!_.isEmpty(filteredDiscussions) && filteredDiscussions.map(d => {
                      const r = getRecipient(d);
                      return (
                        <div key={d.id} style={{ padding: '4px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Checkbox
                            disabled={!r.email}
                            label={`${r.name}${r.email ? ` <${r.email}>` : ' (no email)'}`}
                            checked={selectedDiscussionIds.includes(d.id)}
                            onChange={this.toggleDiscussionId(d.id)}
                          />
                          {r.workflowStatus
                            && <Label size="tiny" basic>{r.workflowStatus}</Label>}
                        </div>
                      );
                    })
                  }
                </div>
              </Segment>
            )}

            <Form.Group widths="equal">
              <Form.Field required error={touched.fromName && !!fromName && !hasFromName}>
                <label>From name</label>
                <Input
                  placeholder="e.g. Dyzio Team"
                  value={fromName}
                  onChange={this.onFromNameChange}
                  onBlur={this.markTouched('fromName')}
                />
              </Form.Field>
              <Form.Field required error={touched.fromEmail && !!fromEmail && !hasFromEmail}>
                <label>From email</label>
                <Input
                  placeholder="e.g. team@dyzio.co"
                  value={fromEmail}
                  onChange={this.onFromEmailChange}
                  onBlur={this.markTouched('fromEmail')}
                />
                {touched.fromEmail && !!fromEmail && !hasFromEmail && (
                  <p className="small error" style={{ color: '#9f3a38', marginTop: 4 }}>
                    That doesn't look like a valid email.
                  </p>
                )}
              </Form.Field>
            </Form.Group>

            <Form.Field required error={touched.replyTo && !!replyTo && !hasReplyTo}>
              <label>Reply-to</label>
              <Input
                fluid
                placeholder="Replies will go to this address"
                value={replyTo}
                onChange={this.onReplyToChange}
                onBlur={this.markTouched('replyTo')}
              />
              {touched.replyTo && !!replyTo && !hasReplyTo && (
                <p className="small error" style={{ color: '#9f3a38', marginTop: 4 }}>
                  That doesn't look like a valid email.
                </p>
              )}
            </Form.Field>

            <Form.Field>
              <label>
                Subject
                <span style={{ color: '#999', fontWeight: 'normal', marginLeft: 6, fontSize: 12 }}>
                  (variables supported)
                </span>
              </label>
              <Input
                fluid
                placeholder="e.g. Hi {{name}}, an update on {{campaign_name}}"
                value={subject}
                onChange={this.onSubjectChange}
                onFocus={this.onSubjectFocus}
              />
            </Form.Field>

            {unknownVariables.length > 0 && (
              <div style={{
                background: '#fffaf3',
                border: '1px solid #f2c037',
                borderLeft: '4px solid #f2c037',
                borderRadius: 4,
                padding: '10px 14px',
                marginBottom: 14,
                fontSize: 13,
                color: '#573a08',
              }}
              >
                <Icon name="warning sign" style={{ marginRight: 6 }} />
                Unknown variable{unknownVariables.length === 1 ? '' : 's'} in subject or message:{' '}
                <strong>{unknownVariables.map(t => `{{${t}}}`).join(', ')}</strong>.
                {' '}They will be sent as literal text.
                {' '}Available: <code>{SUPPORTED_VARIABLE_NAMES.join(', ')}</code>.
              </div>
            )}

            <Form.Field>
              <label>Message</label>
              <ReactQuill
                ref={el => { this._quillInstance = el; }}
                value={body}
                onChange={this.onBodyChange}
                onFocus={this.onBodyFocus}
                onChangeSelection={this.onQuillSelectionChange}
              />
            </Form.Field>

            <Form.Field style={{ marginTop: -4 }}>
              <label style={{ marginBottom: 4 }}>Variables</label>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {VARIABLES.map(t => (
                  <Label
                    key={t.name}
                    as="a"
                    basic
                    onClick={this.insertVariable(t.name)}
                    style={{
                      cursor: 'pointer',
                      marginRight: 8,
                      marginBottom: 4,
                      padding: '6px 10px',
                      fontWeight: 'normal',
                    }}
                  >
                    <code>{`{{${t.name}}}`}</code>
                    <span style={{ color: '#888', marginLeft: 6 }}>{t.desc}</span>
                  </Label>
                ))}
              </div>
            </Form.Field>

            {recipientsWithoutEmail > 0 && (
              <Message warning size="tiny">
                <Icon name="warning sign" />
                {recipientsWithoutEmail} of {recipientCount} selected recipient(s) have no email and will be skipped.
              </Message>
            )}

            {lastResult && lastResult.type === 'queued' && (
              <Message positive size="tiny" onDismiss={() => this.setState({ lastResult: null })}>
                <Icon name="paper plane outline" />
                Queued for {lastResult.queued} recipient{lastResult.queued === 1 ? '' : 's'}.
                {lastResult.skipped > 0 && ` ${lastResult.skipped} skipped (no email).`}
                {' '}Open <strong>View email sending history</strong> from Quick Links to see delivery status.
              </Message>
            )}

            {lastResult && lastResult.type === 'test-queued' && (
              <Message positive size="tiny" onDismiss={() => this.setState({ lastResult: null })}>
                <Icon name="paper plane" />
                Test email queued — should arrive at {currentUserEmail} shortly.
              </Message>
            )}

            {lastResult && lastResult.type === 'error' && (
              <Message negative size="tiny" onDismiss={() => this.setState({ lastResult: null })}>
                <Icon name="exclamation circle" />
                {lastResult.message || 'Send failed.'}
              </Message>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          {confirmingSend ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#666' }}>
                <Icon name="question circle outline" />
                Send to <strong>{recipientCount}</strong> recipient{recipientCount === 1 ? '' : 's'}?
                {unknownVariables.length > 0 && ' Unknown variables will be sent as literal text.'}
              </span>
              <div>
                <Button onClick={this.handleCancelConfirm} disabled={isSending}>No, go back</Button>
                <Button
                  color="pink"
                  loading={isSending}
                  disabled={isSending}
                  onClick={this.handleConfirmSend}
                >
                  Yes, send now
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                type="button"
                loading={isSending}
                disabled={isSending || !canSendTest}
                onClick={this.handleSendTestToMe}
                style={{
                  background: 'transparent',
                  color: '#2185d0',
                  padding: '8px 0',
                  boxShadow: 'none',
                  fontWeight: 'normal',
                }}
              >
                {currentUserEmail
                  ? `Send test to me (${currentUserEmail})`
                  : 'Send test to me'}
              </Button>
              <div>
                <Button onClick={onClose} disabled={isSending}>Cancel</Button>
                <Button
                  color="pink"
                  loading={isSending}
                  disabled={isSending || !canSend}
                  onClick={this.handleSendClick}
                >
                  Send now
                </Button>
              </div>
            </div>
          )}
        </Modal.Actions>
      </Modal>
    );
  }
}

AdHocEmailModal.propTypes = {
  campaignId: PropTypes.string.isRequired,
  campaignName: PropTypes.string,
  discussions: PropTypes.array,
  // Used only for the "Send test to me" UI helper — backend identity comes
  // from the verified JWT in the Authorization header, not from these props.
  currentUserEmail: PropTypes.string,
  currentUserName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

AdHocEmailModal.defaultProps = {
  campaignName: '',
  discussions: [],
  currentUserEmail: '',
  currentUserName: '',
};

export default AdHocEmailModal;



/** WEBPACK FOOTER **
 ** ./src/components/ad-hoc-email-modal/index.js
 **/